import { useRef, useState, type DragEvent } from 'react';
import { Film, ShieldCheck, Upload } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { FILE_ACCEPT } from '@/constants/media';
import { openVideoFile } from '@/features/project/projectActions';
import { cn } from '@/utils/cn';

export function UploadDropzone() {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const dragDepth = useRef(0);

  const handleFile = async (file: File | undefined) => {
    if (!file || busy) return;
    setBusy(true);
    try {
      await openVideoFile(file);
    } finally {
      setBusy(false);
    }
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    dragDepth.current = 0;
    setDragOver(false);
    void handleFile(event.dataTransfer.files?.[0]);
  };

  const onDragEnter = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    dragDepth.current += 1;
    setDragOver(true);
  };

  const onDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragOver(false);
  };

  const openPicker = () => inputRef.current?.click();

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label="Upload a video: drop a file here or press Enter to browse"
      aria-busy={busy || undefined}
      onClick={openPicker}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          openPicker();
        }
      }}
      onDragEnter={onDragEnter}
      onDragOver={(event) => event.preventDefault()}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      data-testid="upload-dropzone"
      className={cn(
        'group relative cursor-pointer rounded-2xl border-2 border-dashed p-10 text-center transition-all sm:p-14',
        dragOver
          ? 'scale-[1.01] border-signal-500 bg-signal-500/5'
          : 'border-ink-300 bg-white/60 hover:border-ink-400 dark:border-ink-600 dark:bg-ink-900/60 dark:hover:border-ink-500',
      )}
    >
      <input
        ref={inputRef}
        type="file"
        accept={FILE_ACCEPT}
        className="sr-only"
        aria-hidden
        tabIndex={-1}
        onChange={(event) => {
          void handleFile(event.target.files?.[0]);
          event.target.value = ''; // allow re-selecting the same file
        }}
      />

      <div className="mx-auto flex max-w-sm flex-col items-center gap-4">
        <div
          className={cn(
            'grid size-14 place-items-center rounded-2xl bg-ink-100 text-ink-500 transition-colors dark:bg-ink-800 dark:text-ink-300',
            dragOver && 'bg-signal-500/15 text-signal-600 dark:text-signal-400',
          )}
        >
          {busy ? (
            <Film aria-hidden className="size-7 animate-pulse" />
          ) : (
            <Upload aria-hidden className="size-7" />
          )}
        </div>

        <div>
          <p className="font-display text-lg font-semibold tracking-tight">
            {busy ? 'Reading video…' : dragOver ? 'Drop to load' : 'Drop a video here'}
          </p>
          <p className="mt-1 text-sm text-ink-500 dark:text-ink-400">
            MP4, WebM, MOV, MKV, OGV or AVI · up to 1 GB
          </p>
        </div>

        <Button
          size="md"
          variant="secondary"
          loading={busy}
          onClick={(event) => {
            event.stopPropagation();
            openPicker();
          }}
        >
          Choose a file
        </Button>

        <p className="flex items-center gap-1.5 text-xs text-ink-400 dark:text-ink-500">
          <ShieldCheck aria-hidden className="size-3.5" />
          Converted on this device — nothing uploads.
        </p>
      </div>
    </div>
  );
}
